import React, { Component } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import styles from "./styles.module.scss";

// Displays a placeholder "skeleton" div while the real image is loading.

// This component can be paired with <LazyLoad /> as seen below to both lazy load
// images and display a "skeleton" until it is fully loaded like so:
// <LazyLoad height={arbitraryNumber}>
//   <SkeletonImage src="foo" skeletonHeight={arbitraryNumber} />
// </LazyLoad>

// The "placeholder" prop of the 'react-lazyload' <LazyLoad /> component
// doesn't provide this functionality, as it will immediately display the
// image once it's scrolled into view. This behaviour does not give the
// image time to load and causes loading images to "pop in" and move
// things around on the screen.

export default class SkeletonImage extends Component {
  constructor(props) {
    super(props);
    this.state = { finishedLoading: false };
  }

  componentDidMount() {
    // Because eager loading sometimes means the image has loaded before the component finishes mounting, and thus onLoad never gets fired
    const { imageRef } = this;
    if (imageRef.complete && imageRef.naturalHeight !== 0) {
      this.setState({ finishedLoading: true });
    }
  }

  handleLoad = () => {
    const { onLoad } = this.props;
    this.setState({ finishedLoading: true });
    // As this component mimics a traditional image we should allow a user
    // to pass their own onLoad.
    onLoad();
  };

  render() {
    const { style, height, src, loading } = this.props;
    const { finishedLoading } = this.state;
    const newStyle = { ...style };
    const { skeletonClassName, skeletonStyle, skeletonHeight, ...props } =
      this.props;
    if (!finishedLoading) newStyle.visibility = "hidden";
    if (!skeletonHeight) newStyle.height = `${height}px`;
    const isDefaultImage = [
      "/nextpublic/zeroState-mobile@2x.png",
      "/nextpublic/zeroState@2x.png",
    ].includes(src);
    // Render a hidden image so it begins loading.
    // Once it has finished loading the `onLoad` handler will be called
    // which will hide the skeleton and display the image.
    return (
      <>
        <span {...props} style={newStyle}>
          {isDefaultImage && (
            <div className={styles.defaultImageCopy}>
              <p>
                We&apos;re in the process of <br />
                getting photos for this space.
              </p>
            </div>
          )}
          {/* Upgrading to Next 11, disabling this so the upgrade can happen  */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt=""
            src={src}
            loading={loading}
            fetchpriority="high"
            onLoad={this.handleLoad}
            ref={(c) => {
              this.imageRef = c;
            }}
          />
        </span>
        {!finishedLoading && (
          <div
            className={cn(styles.skeleton, styles.image, skeletonClassName)}
            styles={skeletonStyle}
          />
        )}
      </>
    );
  }
}

SkeletonImage.propTypes = {
  skeletonClassName: PropTypes.string,
  skeletonStyle: PropTypes.shape({}),
  skeletonHeight: PropTypes.string,
  onLoad: PropTypes.func,
  style: PropTypes.shape({}),
  height: PropTypes.string,
  onClick: PropTypes.func,
  loading: PropTypes.string,
  src: PropTypes.string,
};

SkeletonImage.defaultProps = {
  onClick: () => {},
  onLoad: () => {},
  style: {},
  skeletonClassName: null,
  skeletonStyle: null,
  skeletonHeight: "",
  height: "",
  src: "",
  loading: "eager",
};
